import { Icon, InputAdornment } from '@material-ui/core';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import ConfirmationDialog from 'components/Utility/Dialogs/ConfirmationDialog.jsx';
import TabbFeesTooltip from 'components/Utility/TooltipModal/TabbFeesTooltip';
import * as Doka from 'libs/doka/doka.esm.min';
import 'libs/doka/doka.min.css';
import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { BarLoader } from 'react-spinners';
import styled from 'styled-components';
import {
  FetchVenueByParticipantIdAPIRequest,
  UploadVenueImageAPIRequest,
} from 'utils/constants/network';
import { ServiceTypes } from 'utils/constants/venues';
import DeleteBin from 'utils/images/bin.svg';
import { callAPI } from 'utils/network';
import CenteredSpinner from '../../../../../Utility/CenteredSpinner';
import AddButton from '../../../MenuEditor/components/shared/AddButton';
import Button from '../../../MenuEditor/components/shared/Button';
import Checkbox from '../../../MenuEditor/components/shared/Checkbox';
import DeleteButton from '../../../MenuEditor/components/shared/DeleteButton';
import { DISPLAY_MODE } from '../../constants';
import ParticipantDetails from './ParticipantDetails';
import ServiceTypeSelect from './ServiceTypeSelect';

const Container = styled.div`
  margin-top: 30px;
  margin-left: 32px;
  margin-right: 20px;
  max-width: 1120px;
`;

const Form = styled.form``;

const CTAButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin-bottom: 25px;

  @media only screen and (min-device-width: 320px) and (max-device-width: 480px) {
    gap: 5px;
  }
`;

const DeleteIcon = styled.img`
  width: 14px;
  cursor: pointer;
`;
const DeleteText = styled.p`
  margin-top: 16px;
  text-align: center;
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  letter-spacing: -0.2px;
  @media only screen and (max-width: 900px) {
    display: none;
  }
`;
const DeleteImageButtonWrapper = styled.div`
  margin: 8px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  cursor: pointer;
`;

const ButtonLabel = styled.p`
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  letter-spacing: -0.2px;
  color: #797979;
`;

const UploadPara = styled.p`
  text-align: 'center';
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 13px;
  text-align: center;
  letter-spacing: -0.2px;
  color: #000000;
`;

const AddButtonContainer = styled.div`
  padding: 25px 10px 0px 0px;
  margin-top: -20px;
  margin-bottom: 25px;
`;

const BinBorder = styled.div`
  text-align: left !important;
  display: flex;
  align-items: center;
  width: 30px;
  height: 30px;
  cursor: pointer;
  clear: both;
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 15px;
  padding: 5px 0px 5px 7px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media only screen and (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const Wrapper = styled.div``;

const ImageWrapper = styled.div`
  display: flex;
`;
const ImageBox = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #afafaf;
  overflow: hidden;
  margin-right: 16px;

  @media only screen and (max-width: 900px) {
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: relative;
  }
`;
const SaveVenue = styled.div`
  font-size: 13px;
  text-align: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const doka = Doka.create(null, {
  outputType: 'image/jpeg',
  outputFit: 'cover',
  outputQuality: 80,
  outputWidth: 500,
  outputHeight: 500,
  size: { width: 500, height: 500 },
  cropAspectRatio: 1,
});

const GroupEditor = props => {
  const { onSave, onCancel, loading, displayMode, group, onDelete, showAlert } =
    props;

  const isEditMode = displayMode === DISPLAY_MODE.edit;
  const [emptySlug, setEmptySlug] = useState(false);
  const uploadImageRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingParticipant, setUploadingParticipant] = useState(false);
  const [addVenueField, setAddVenueField] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [fetchingParticipantDetails, setFetchingParticipantDetails] =
    useState(false);

  const formRef = useRef();
  const setDefaultValues = () => {
    if (isEditMode) {
      return group;
    }
    return {
      slug: '',
      expandedParticipants: [],
      participants: [],
      serviceType: ServiceTypes.TABLE_AND_COLLECTION,
    };
  };
  const {
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...setDefaultValues(),
    },
  });

  useEffect(() => {
    reset(setDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, displayMode]);

  const {
    fields,
    append,
    remove: deleteParticipant,
    move,
  } = useFieldArray({
    control,
    name: 'participants',
  });

  const disableForm = loading;

  const slug = watch('slug');
  const serviceType = watch('serviceType');
  const offsetFee = watch('offsetFee');
  const participantToAdd = watch('participantToAdd');
  const imageUrl = watch('imageUrl');

  const convertVenueImageUrlToCDNUrl = (imageUrl, width, height) => {
    const imageKey = imageUrl.split('/').pop();
    return `https://ik.imagekit.io/fatsomatabb/${
      process.env.REACT_APP_API_STAGE === 'prod' ? 'production/' : ''
    }${imageKey}?tr=w-${width},h-${height}`;
  };

  useEffect(() => {
    if (imageUrl) {
      setValue('imageUrl', convertVenueImageUrlToCDNUrl(imageUrl, 64, 64));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItemImageViaApi = async path => {
    setValue('imageUrl', path);
  };

  const removeFile = async () => {
    setUploadingImage(true);

    try {
      setValue('imageUrl', null);
      await setItemImageViaApi(null);
    } catch (e) {
      alert(`We could not remove the image: ${e.toString()}`);
    }

    setUploadingImage(false);
  };

  const onChangeFile = async event => {
    event.stopPropagation();
    event.preventDefault();

    const fileReader = new FileReader();
    const file = event.target.files[0];

    fileReader.onload = async () => {
      setUploadingImage(true);

      const apiRequest = UploadVenueImageAPIRequest({
        file_base64_data: fileReader.result,
        purpose: 'Menu Image',
      });

      try {
        const { file } = await callAPI(apiRequest);
        setValue('imageUrl', convertVenueImageUrlToCDNUrl(file, 64, 64));

        await setItemImageViaApi(file);
      } catch (e) {
        setValue('imageUrl', null);
        alert(`We could not upload the image: ${e.toString()}`);
      }

      setUploadingImage(false);
    };

    fileReader.onerror = () => {
      fileReader.abort();
    };

    doka.edit(file).then(output => {
      fileReader.readAsDataURL(output.file);
    });
  };

  const FetchVenueByParticipantId = async () => {
    setFetchingParticipantDetails(true);
    const participantDetails = { found: false, participant: null };

    try {
      const apiRequest = FetchVenueByParticipantIdAPIRequest({
        participant_id: participantToAdd.trim(),
      });
      const response = await callAPI(apiRequest);
      participantDetails.found = Object.keys(response.Venue).length > 0;
      participantDetails.participant = response.Venue;
    } catch (e) {
      console.error(e);
      showAlert(
        'Failure',
        `Sorry, we could not find the venue with shortcode ${participantToAdd}.`
      );
    } finally {
      setFetchingParticipantDetails(false);
      setValue('participantToAdd', '');
      return participantDetails;
    }
  };

  if (slug === '' && !emptySlug) {
    setEmptySlug(true);
  }

  const onSubmit = data => {
    const newData = {
      ...data,
      participants: data.participants.map(p => p.participantId),
    };

    if (onSave) {
      onSave(newData);
    }
  };

  const onDeleteGroup = async () => {
    onDelete(group);
    setShowDeleteConfirmation(false);
  };

  const onFormCancel = () => {
    reset(); // reset form state to initial values

    if (onCancel) {
      onCancel();
    }
  };

  const changeServiceType = e => {
    setValue('serviceType', e.target.value);
  };

  const convertToSlug = value =>
    value
      .replaceAll(' ', '-')
      .replace(/[^A-Za-z0-9\-]/g, '')
      .toLowerCase();

  const autoSlugByName = value => {
    if (emptySlug) {
      setValue('slug', convertToSlug(value));
    }
  };

  const getCommonTextfieldProps = (field, valMessage) => ({
    error: !!errors[field],
    helperText:
      errors[field] && errors[field].message
        ? errors[field].message
        : errors[field] && errors[field].validation
        ? errors[field].validation
        : '',
    disabled: disableForm,
  });

  const handleAddVenue = async () => {
    if (fetchingParticipantDetails) {
      return;
    }

    try {
      setUploadingParticipant(true);
      const { found, participant } = await FetchVenueByParticipantId();
      const doesVenueExist = fields.find(
        participant => participant.participantId === participantToAdd
      );
      if (doesVenueExist) {
        showAlert(
          'Failure',
          `Sorry, venue with shortcode ${participantToAdd} is already added.`
        );
        setUploadingParticipant(false);
        return;
      }

      if (!found) {
        showAlert(
          'Failure',
          `Sorry, we could not find the venue with shortcode ${participantToAdd}.`
        );
        setUploadingParticipant(false);
        return;
      }
      const { name, slug, image_url, participant_id } = participant;
      append({
        participantId: participant_id,
        name: name,
        slug,
        imageUrl: image_url,
      });
    } catch (err) {
      console.log(err);
    }
    setUploadingParticipant(false);
  };

  const reorderCards = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.source && !!result.destination) {
      if (result.destination.index === result.source.index) {
        return;
      }
      move(result.source.index, result.destination.index);
    }
  };

  const grid = 0;
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: 0 * grid,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? 'lightgreen' : 'transparent',

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  return (
    <Container>
      <div
        style={{ color: 'rgba(0, 0, 0, 0.54)', paddingBottom: '12px' }}
        className="input__label"
      >
        Image
      </div>
      <ImageWrapper>
        <ImageBox
          onClick={e => {
            e.stopPropagation();
            uploadImageRef.current.click();
          }}
          style={{
            display: 'flex',
            justifyContent: imageUrl && 'flex-end',
            alignItems: imageUrl && 'start',
            background:
              imageUrl && !uploadingImage
                ? `url(${imageUrl}) center center`
                : 'white',
            backgroundSize: 'cover',
          }}
        >
          <input
            id="myInput"
            type="file"
            ref={uploadImageRef}
            style={{ display: 'none' }}
            onChange={onChangeFile}
          />
          {!imageUrl && !uploadingImage && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Icon
                style={{
                  width: 26,
                  height: 24,
                  alignSelf: 'center',
                }}
              >
                add_photo_alternate
              </Icon>
              <UploadPara>Upload Image</UploadPara>
            </div>
          )}
          {uploadingImage && <BarLoader color="#000" width={100} height={4} />}
          {imageUrl && !uploadingImage && (
            <DeleteImageButtonWrapper>
              <DeleteButton
                onClick={e => {
                  e.stopPropagation();
                  removeFile();
                }}
              />
            </DeleteImageButtonWrapper>
          )}
        </ImageBox>
      </ImageWrapper>
      <FormWrapper>
        <Form
          autoComplete="off"
          autoCorrect="off"
          spellCheck="off"
          className="settings__section__details__form"
          onSubmit={handleSubmit(onSubmit)}
          ref={formRef}
        >
          <Wrapper>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is Required' }}
              render={({ field }) => (
                <TextField
                  {...getCommonTextfieldProps('name')}
                  onInput={e => autoSlugByName(e.target.value)}
                  classes={{ root: 'marginb__md group__form__field__small' }}
                  InputLabelProps={{
                    shrink: true,
                    classes: { root: 'input__label' },
                  }}
                  label="Name"
                  fullWidth
                  autoCorrect="off"
                  spellCheck="off"
                  autoComplete="off"
                  type="text"
                  margin="normal"
                  placeholder=""
                  {...field}
                />
              )}
            />
          </Wrapper>
          <Wrapper>
            <Controller
              name="slug"
              control={control}
              rules={{ required: 'URL is Required' }}
              render={({ field }) => (
                <TextField
                  {...getCommonTextfieldProps('slug')}
                  style={{ maxWidth: 550 }}
                  onInput={e =>
                    (e.target.value = convertToSlug(e.target.value))
                  }
                  classes={{ root: 'marginb__md group__form__field__small' }}
                  fullWidth
                  autoCorrect="off"
                  spellCheck="off"
                  autoComplete="off"
                  type="text"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                    classes: { root: 'input__label' },
                  }}
                  label="Page URL"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        disableTypography={true}
                        classes={{ root: 'group__form__field__adornment' }}
                        position="start"
                      >
                        {`${process.env.REACT_APP_CUSTOMER_APP_URL}/m/`}
                      </InputAdornment>
                    ),
                  }}
                  {...field}
                />
              )}
            />
          </Wrapper>
          <Wrapper>
            <Controller
              name="description"
              control={control}
              multiline
              defaultValue=""
              rules={{ required: false }}
              render={({ field }) => (
                <TextField
                  {...getCommonTextfieldProps('description')}
                  disabled={disableForm}
                  classes={{ root: 'marginb__md group__form__field__small' }}
                  fullWidth
                  autoCorrect="off"
                  spellCheck="off"
                  autoComplete="off"
                  type="text"
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                    classes: { root: 'input__label' },
                  }}
                  label="Description"
                  placeholder=""
                  {...field}
                />
              )}
            />
          </Wrapper>

          <FormLabel classes={{ root: 'input__label marginb__sm' }}>
            Service Type
          </FormLabel>

          <ServiceTypeSelect
            name="serviceType"
            disabled={disableForm}
            value={serviceType}
            onChange={e => changeServiceType(e)}
          />
          <div className="marginb__md" />

          <FormLabel classes={{ root: 'input__label marginb__sm' }}>
            Fatsoma Tabb fee
            <TabbFeesTooltip />
          </FormLabel>

          <Checkbox
            disabled={disableForm}
            color="primary"
            name="offsetFee"
            onSelect={() => setValue('offsetFee', !offsetFee)}
            isChecked={offsetFee}
            label="Add fee to customer orders"
            style={{ height: 35 }}
          ></Checkbox>

          <div className="marginb__sm" />
          <ButtonLabel>Venues </ButtonLabel>
          <div style={fields.length ? { marginBottom: '20px' } : {}}>
            <DragDropContext onDragEnd={reorderCards}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {fields.map((field, index) => {
                      const { participantId, name, slug, imageUrl } = field;
                      return (
                        <Draggable
                          key={participantId}
                          draggableId={participantId}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                            >
                              <ParticipantDetails
                                id={participantId}
                                index={index}
                                name={name}
                                slug={slug}
                                imageSrc={imageUrl}
                                remove={() =>
                                  deleteParticipant(
                                    fields.findIndex(
                                      participant =>
                                        participant.participantId ===
                                        participantId
                                    )
                                  )
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {addVenueField && (
            <Wrapper
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '13rem',
              }}
            >
              <Controller
                name="participantToAdd"
                control={control}
                multiline
                defaultValue=""
                rules={{ required: false }}
                render={({ field }) => (
                  <TextField
                    {...getCommonTextfieldProps('participantToAdd')}
                    disabled={disableForm}
                    classes={{
                      root: 'marginb__md client__form__field__small ',
                    }}
                    multiline
                    autoCorrect="off"
                    spellCheck="off"
                    autoComplete="off"
                    type="text"
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: 'input__label settings__section__details__form__venue__id',
                      },
                    }}
                    label="Venue ID"
                    placeholder=""
                    {...field}
                    onKeyPress={event => {
                      if (event.key === 'Enter') {
                        handleAddVenue();
                      }
                    }}
                  />
                )}
              />
              <Wrapper
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  marginLeft: 15,
                }}
              >
                {uploadingParticipant ? (
                  <CenteredSpinner />
                ) : (
                  <React.Fragment>
                    <SaveVenue
                      className="fatsoma__venue__id"
                      onClick={handleAddVenue}
                    >
                      Save
                    </SaveVenue>
                    <BinBorder onClick={() => setAddVenueField(false)}>
                      <DeleteIcon src={DeleteBin} alt="Delete" />
                    </BinBorder>
                  </React.Fragment>
                )}
              </Wrapper>
            </Wrapper>
          )}

          <Wrapper>
            <AddButtonContainer>
              <AddButton onClick={() => setAddVenueField(true)}>
                Add a venue
              </AddButton>
            </AddButtonContainer>
          </Wrapper>

          <Wrapper>
            <CTAButtonsContainer>
              <ActionButtons>
                {loading ? (
                  <div style={{ minWidth: '95px' }}>
                    <CenteredSpinner />
                  </div>
                ) : (
                  <Button
                    className="fatsoma__cta"
                    style={{ maxHeight: '46px' }}
                    disabled={disableForm}
                    type="submit"
                    onClick={() => {
                      formRef.current.dispatchEvent(
                        new Event('submit', { cancelable: true })
                      );
                    }}
                  >
                    Save
                  </Button>
                )}

                <Button
                  className="fatsoma__cta fatsoma__cta__secondary"
                  onClick={onFormCancel}
                  type="reset"
                  style={{ maxHeight: '46px' }}
                >
                  Cancel
                </Button>
              </ActionButtons>
              {isEditMode && !loading && (
                <IconWrapper
                  onClick={() => setShowDeleteConfirmation(true)}
                  style={{ marginRight: 2 }}
                >
                  <DeleteIcon src={DeleteBin} alt="Delete" />
                  <DeleteText style={{ marginLeft: 7 }}>
                    Delete multi-venue
                  </DeleteText>
                </IconWrapper>
              )}
            </CTAButtonsContainer>
          </Wrapper>
        </Form>
      </FormWrapper>
      <ConfirmationDialog
        open={showDeleteConfirmation}
        variant="yesNo"
        onConfirm={onDeleteGroup}
        onClose={() => {
          setShowDeleteConfirmation(false);
        }}
        confirmationDescription=""
        confirmationTitle="Confirm you want to delete this multi-venue"
      />
    </Container>
  );
};

export default GroupEditor;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MultiVenue/components/GroupEditor/index.jsx